/**
 * Analytics API Module
 *
 * Centralized API calls for analytics endpoints
 */

import apiClient from '@/lib/api-client'

export interface AnalyticsParams {
  start?: string
  end?: string
  tz?: string
  limit?: number
  offset?: number
  sort_by?: string
  tenantId?: string
  organizationId?: string
}

export interface PageMeta {
  total: number
  limit: number
  offset: number
}

export interface AnalyticsOverviewResponse {
  dateRange: { start: string; end: string; tz: string }
  counts: {
    signupsToday: number
    signupsWeek: number
    agentsCreatedToday: number
    agentsCreatedWeek: number
    agentsUsedToday: number
    agentsUsedWeek: number
    transactionsToday: number
    transactionsWeek: number
    messagesToday: number
    messagesWeek: number
  }
  tokens: {
    prompt: number
    completion: number
    total: number
    totalValue: number
  }
  top: {
    agents: Array<{ id: string; name: string; conversations: number }>
    models: Array<{ model: string; count: number }>
  }
}

export interface TimeseriesResponse {
  granularity: string
  buckets: Array<{ date: string; value: number }>
}

export interface AgentUsageItem {
  id: string
  name: string
  description?: string
  category?: string
  conversations: number
  uniqueUsers: number
  lastActive?: string
}

export interface AgentUsageResponse {
  items: AgentUsageItem[]
  page: PageMeta
}

export interface UserMetricItem {
  id: string
  email: string
  username?: string
  name?: string
  createdAt: string
  conversations: number
  messages: number
  transactions: number
  totalTokens: number
  promptTokens: number
  completionTokens: number
  uniqueAgents: number
  totalCost: number
}

export interface UserMetricsResponse {
  items: UserMetricItem[]
  page: PageMeta
}

export interface TransactionItem {
  model: string
  totals: {
    count: number
    tokens: {
      prompt: number
      completion: number
      total: number
    }
    value: number
  }
}

export interface TransactionsResponse {
  items: TransactionItem[]
  page: PageMeta
}

export interface MessageItem {
  messageId: string
  conversationId: string
  sender: string
  isInput: boolean
  inputTokens: number
  outputTokens: number
  totalTokens: number
  textGlimpse: string
  model: string
  cost: number
  createdAt: string
  hasAttachments: boolean
}

export interface MessagesResponse {
  items: MessageItem[]
  page: PageMeta
}

export interface ConversationItem {
  conversationId: string
  userId: string
  userName: string
  userEmail: string
  agentId?: string
  agentName?: string
  isCustomAgent: boolean
  model: string
  promptTokens: number
  completionTokens: number
  totalTokens: number
  totalValue: number
  overallLatencyMs: number
  averageTurnLatencyMs: number
  userMessageCount: number
  assistantMessageCount: number
  totalMessageCount: number
  hasAttachments: boolean
  conversationStartedAt: string
  lastMessageAt: string
  messages: any[]
}

export interface ConversationsResponse {
  items: ConversationItem[]
  page: PageMeta
}

export const analyticsApi = {
  /**
   * Get analytics overview with counts and top items
   */
  getOverview: async (params: AnalyticsParams = {}): Promise<AnalyticsOverviewResponse> => {
    const { data } = await apiClient.get<AnalyticsOverviewResponse>('/v1/analytics/overview', {
      params: {
        start: params.start,
        end: params.end,
        tz: params.tz,
        tenantId: params.tenantId,
        organizationId: params.organizationId,
      },
    })
    return data
  },

  /**
   * Get timeseries data for a specific metric
   */
  getTimeseries: async (metric: string, params: AnalyticsParams = {}): Promise<TimeseriesResponse> => {
    const { data } = await apiClient.get<TimeseriesResponse>('/v1/analytics/timeseries', {
      params: {
        metric,
        start: params.start,
        end: params.end,
        tenantId: params.tenantId,
        organizationId: params.organizationId,
      },
    })
    return data
  },

  /**
   * Get agent usage analytics
   */
  getAgentUsage: async (params: AnalyticsParams = {}): Promise<AgentUsageResponse> => {
    const { data } = await apiClient.get<AgentUsageResponse>('/v1/analytics/agents', {
      params: {
        start: params.start,
        end: params.end,
        limit: params.limit,
        offset: params.offset,
        tenantId: params.tenantId,
        organizationId: params.organizationId,
      },
    })
    return data
  },

  /**
   * Get user metrics
   */
  getUserMetrics: async (params: AnalyticsParams = {}): Promise<UserMetricsResponse> => {
    const { data } = await apiClient.get<UserMetricsResponse>('/v1/analytics/users', {
      params: {
        start: params.start,
        end: params.end,
        limit: params.limit,
        offset: params.offset,
        sort_by: params.sort_by,
        tenantId: params.tenantId,
        organizationId: params.organizationId,
      },
    })
    return data
  },

  /**
   * Get transaction summary
   */
  getTransactions: async (params: AnalyticsParams = {}): Promise<TransactionsResponse> => {
    const { data } = await apiClient.get<TransactionsResponse>('/v1/analytics/transactions', {
      params: {
        start: params.start,
        end: params.end,
        limit: params.limit,
        offset: params.offset,
        tenantId: params.tenantId,
        organizationId: params.organizationId,
      },
    })
    return data
  },

  /**
   * Get message analytics
   */
  getMessages: async (params: AnalyticsParams = {}): Promise<MessagesResponse> => {
    const { data } = await apiClient.get<MessagesResponse>('/v1/analytics/messages', {
      params: {
        start: params.start,
        end: params.end,
        limit: params.limit,
        offset: params.offset,
        tenantId: params.tenantId,
        organizationId: params.organizationId,
      },
    })
    return data
  },

  /**
   * Get conversation analytics
   */
  getConversations: async (params: AnalyticsParams = {}): Promise<ConversationsResponse> => {
    const { data } = await apiClient.get<ConversationsResponse>('/v1/analytics/conversations', {
      params: {
        start: params.start,
        end: params.end,
        limit: params.limit,
        offset: params.offset,
        tenantId: params.tenantId,
        organizationId: params.organizationId,
      },
    })
    return data
  },
}
